import type { Notification } from '../../types';
import { apiClient, handleApiError, type PaginationMetadata } from '../api-client';

/**
 * Get notifications with pagination
 */
export const getNotifications = async (
  page: number,
  limit: number
): Promise<{
  items: Notification[];
  pagination: PaginationMetadata;
  unreadCount: number;
  hasMore: boolean;
}> => {
  try {
    const {
      items,
      pagination,
      unreadCount = 0,
    } = await apiClient.getPaginated<Notification, { unreadCount: number }>('/notifications', { page, limit });

    return {
      items,
      pagination,
      unreadCount,
      hasMore: pagination.currentPage + 1 < pagination.totalPages,
    };
  } catch (error) {
    return handleApiError(error, 'Error fetching notifications');
  }
};

/**
 * Get unread notification count
 */
export const getNotificationCount = async (): Promise<{ count: number }> => {
  try {
    return apiClient.get<{ count: number }>('/notifications/count');
  } catch (error) {
    return handleApiError(error, 'Error fetching notification count');
  }
};

/**
 * Mark a notification as read
 */
export const markNotificationAsRead = async (notificationId: string): Promise<Notification> => {
  try {
    return apiClient.patch<Notification>(`/notifications/${notificationId}/read`);
  } catch (error) {
    return handleApiError(error, 'Error marking notification as read');
  }
};

/** Maximum comment IDs the server accepts per mark-as-read request. */
export const MAX_COMMENT_IDS_PER_READ = 100;

/**
 * Mark the current user's notifications about the given comments as read
 */
export const markCommentNotificationsAsRead = async (commentIds: string[]): Promise<{ count: number }> => {
  try {
    return apiClient.post<{ count: number }>('/notifications/comments/read', { commentIds });
  } catch (error) {
    return handleApiError(error, 'Error marking comment notifications as read');
  }
};

/**
 * Mark all notifications as read
 */
export const markAllNotificationsAsRead = async (): Promise<{ message: string }> => {
  try {
    return apiClient.patch<{ message: string }>('/notifications/read-all');
  } catch (error) {
    return handleApiError(error, 'Error marking all notifications as read');
  }
};
